import { useCallback, useEffect } from "react";
// import { skipTensionLanding } from "../Games/Shared/tensionSymbols"; // the landing gate, off (2026-09-30)
import { Settings } from "../settings";
import { COIN_SYMBOL_IDS, COIN_JACKPOT_SYMBOL_ID, isCoinSymbol, isJackpotSymbol, getJackpotTier, isBonusSymbol, getSymbolZIndex, ZEUS_COLLECT_ID, ZEUS_DOUBLE_ID, ZEUS_MYSTERY_ID, cellZIndex, COIN_SYMBOL_ID } from "../SymbolData";
import { coinValueAt } from "../Games/Shared/coinValues";
import { anticipationWaitsFor } from "../Games/Shared/scatterAnticipation";
import { soundManager } from "../SoundManager";
import { SOUND_NAME } from "../sounds";
import { useReelRefs } from "../Contexts/ReelRefsContext";
import { FEATURES_CONFIG } from "../featuresConfig";
import store from "../State/Store/store";
import { wearJackpotCoinSkin } from "../Games/Shared/jackpotCoinSkin";
import { isTurboLike, isSuperTurbo } from "../utils/speedHelpers";
import { isSpinHeld, onSpinHoldRelease } from "../utils/spinHold";
import { restLandedPictures } from "../Games/Shared/turboRest";
import * as PIXI from "pixi.js";
import { waitForBoardToSettle } from "../Games/Shared/waitForBoardToSettle";
import { baseDiag } from "../utils/fsDiag";
import { fireCoinToLogo } from "../Games/Shared/coinToLogoTrail";
import { addLogoPotPoint } from "../Games/Shared/logoPot";

// Reusable point — avoids heap allocation per Zeus symbol landing
const _tempPoint = new PIXI.Point();

// ── Super-turbo sound dedup ──────────────────────────────────────────────────
// All reels land in the same frame in super turbo, so coin/zeus landing sounds
// would stack N× for nothing. This Set tracks which sounds have already fired
// this frame; a single rAF callback clears it for the next frame.
const _frameSoundsPlayed = new Set();
let _frameSoundRAF = null;

function _playOncePerFrame(soundName) {
  if (_frameSoundsPlayed.has(soundName)) {
    return;
  }
  _frameSoundsPlayed.add(soundName);
  soundManager.play(soundName);
  if (!_frameSoundRAF) {
    _frameSoundRAF = requestAnimationFrame(() => {
      _frameSoundsPlayed.clear();
      _frameSoundRAF = null;
    });
  }
}

/**
 * Apply a cell's render priority on the flat overlay.
 *
 * The base-game overlay is one sortable container of 15 sibling cells, so a
 * cell's zIndex orders it against the whole board. Landing is the main place
 * this gets set, but every path that repaints a board without a spin (reload,
 * intro, free-spin exit) has to set it too — otherwise those cells keep the
 * default zIndex and the board silently falls back to left-to-right paint
 * order until the next spin.
 */
const applyOverlayZIndex = (overlayController, symbolIndex, symbolId) => {
  const overlaySymbolContainer =
    overlayController?.overlaySymbolRefs?.[symbolIndex];
  if (overlaySymbolContainer && !overlaySymbolContainer.destroyed) {
    // A plain coin also stacks by its value (the round's, as it shows it).
    const value =
      symbolId === COIN_SYMBOL_ID
        ? coinValueAt(store.getState().game.currentRound, overlayController.reelIndex, symbolIndex)
        : 0;
    overlaySymbolContainer.zIndex = cellZIndex(
      getSymbolZIndex(symbolId),
      overlayController.reelIndex,
      symbolIndex,
      value ?? 0,
    );
  }
};

/**
 * Hook for triggering landing/idle animations on reels.
 * Uses cached animation availability from OverlayReelController for optimized lookups.
 *
 * Turbo mode: plays idle-only animations (lightweight, seamless transition to win).
 * Normal/manual stop: plays full landing→idle sequence.
 */
export const useReelLandingAnimations = () => {
  const {
    reelRefs,
    overlayReelRefs,
    jackpotsControllerRef,
    potChargingAnimatorRef,
    potControllersRef,
    goldenTrailAnimatorRef,
    gameViewControllerRef,
  } = useReelRefs();

  // ── The turbo rest, made SURE of (2026-10-29) ── every landed cell still on
  // its picture goes to its idle / static when the board is a rest board (not
  // held, no win, no trigger): run as each reel lands unheld (the reels before
  // it may have landed held) and when a hold is let go with the board already
  // down. See Games/Shared/turboRest.
  const restTurboBoard = useCallback(
    (skipReel = -1) => {
      if (FEATURES_CONFIG.SPINE_ANIMATIONS.TURBO_LANDING_ENABLED) return;
      const round = store.getState().game.currentRound;
      if (isSpinHeld() || round?.triggerWin || round?.win > 0) return;
      restLandedPictures({
        overlayControllers: (overlayReelRefs.current || []).map((r) =>
          r?.getOverlayReelController?.(),
        ),
        reelControllers: (reelRefs.current || []).map((r) =>
          r?.getReelController?.(),
        ),
        skipReel,
        prepareCell: (overlay, reel, row, symbolId) => {
          applyOverlayZIndex(overlay, row, symbolId);
          if (symbolId === COIN_JACKPOT_SYMBOL_ID) {
            wearJackpotCoinSkin(overlay, store.getState().game.currentRound, reel, row);
          }
        },
      });
    },
    [overlayReelRefs, reelRefs],
  );

  // Let go with the board already down: it rests now.
  useEffect(
    () =>
      onSpinHoldRelease("base", () => {
        const st = store.getState();
        if (st.freeSpins?.active || st.holdAndWin?.active) return;
        restTurboBoard();
      }),
    [restTurboBoard],
  );

  const triggerReelLandingAnimations = useCallback(
    (reelIndex, reelData, betOverride = null, skipRAF = false) => {
      const overlayReelRef = overlayReelRefs.current?.[reelIndex];
      const overlayController = overlayReelRef?.getOverlayReelController();
      const reelController = reelRefs.current?.[reelIndex]?.getReelController();

      if (!overlayController || !reelController || !reelData) {
        return;
      }

      // Turbo mode: idle-only animations (lightweight, seamless win transitions)
      const currentSpeed = store.getState().settings.autoPlaySpeed;
      const isTurbo = isTurboLike(currentSpeed);
      const isSuperTurboMode = isSuperTurbo(currentSpeed);

      const textureHideOps = [];
      // ── Turbo lands on the `static` spines (ported from Catch the Cod,
      // 2026-10-28) ── a single press (or the spin after a hold is let go) with
      // no win: each symbol on its held `static`, above the reel frame. A held
      // run spins straight on, and a win's presentation works on the pictures —
      // both keep the pictures. Once per reel landing.
      // const landsStatic =
      //   !isSpinHeld() && !(store.getState().game.currentRound?.win > 0);
      // No presentation at all, not only no PAY: the win controller presents on
      // `triggerWin` (useWinController), which a 0-pay trigger also raises —
      // the free-spins scatter line, the Hold & Win trigger coins, a retrigger.
      // Those keep the pictures too, so a static never meets a presentation.
      const presentedRound = store.getState().game.currentRound;
      const landsStatic =
        !isSpinHeld() && !presentedRound?.triggerWin && !(presentedRound?.win > 0);
      // Track highest jackpot tier landed this reel (to play bar animation once)
      // Only tracked when JACKPOTS feature is enabled
      let landedJackpotTier = FEATURES_CONFIG.JACKPOTS.ENABLED ? null : undefined;
      const tierOrder = FEATURES_CONFIG.JACKPOTS.ENABLED ? { mini: 1, major: 2, grand: 3 } : null;

      for (
        let symbolIndex = 0;
        symbolIndex < Settings.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        const symbolId = reelData[symbolIndex];

        if (symbolId === undefined) {
          continue;
        }

        // ── Render priority (M > D > C > everything) ──
        // The overlay is one flat container of 15 sibling cells, so a cell's
        // zIndex now orders it against the WHOLE board rather than just its
        // own reel. Set here, from the landed outcome, because this runs
        // before any spine is shown — the cell is already in the right layer
        // by the time its animation appears, with no mid-animation reshuffle.
        //
        // Deliberately above every branch below: the anticipation skip and the
        // turbo texture path both `continue`, and those cells still need a
        // correct low priority. Reassigning all 15 every landing is also what
        // makes this self-healing — an orb's high z can't outlive its round.
        applyOverlayZIndex(overlayController, symbolIndex, symbolId);

        // ── The jackpot coin wears its jackpot's skin ──
        // Cleopatra's Fire: a trigger coin the math made a jackpot lands as
        // `cj` (16); its skin is on the round (PopiPlay RGS markJackpotCoins).
        // Put on before its spine plays.
        if (symbolId === COIN_JACKPOT_SYMBOL_ID) {
          const skin = wearJackpotCoinSkin(
            overlayController,
            store.getState().game.currentRound,
            reelIndex,
            symbolIndex,
          );
          // Its plate reacts as it lands (GDD: jackpot coins "triggering the
          // fixed jackpots" on the side / top of the screen).
          const tier = skin?.replace(/^Jp_/, "");
          if (tier) gameViewControllerRef?.current?.playJackpotHit?.(tier);
        }

        // Track highest jackpot tier landed on this reel for bar animation
        if (FEATURES_CONFIG.JACKPOTS.ENABLED && isJackpotSymbol(symbolId)) {
          const tier = getJackpotTier(symbolId);
          if (tier && (!landedJackpotTier || tierOrder[tier] > tierOrder[landedJackpotTier])) {
            landedJackpotTier = tier;
          }
        }

        // --- POT Charging: Fire particle trail for Zeus symbols ---
        // Placed BEFORE turbo skip — trails must fire in ALL spin modes (normal, turbo, autoplay).
        // Only needs overlay container position (pre-positioned), not a playing spine animation.
        if (FEATURES_CONFIG.POT_CHARGING.ENABLED && isBonusSymbol(symbolId)) {
          const animator = potChargingAnimatorRef?.current;
          const overlaySymbolContainer = overlayController?.overlaySymbolRefs?.[symbolIndex];

          if (animator && overlaySymbolContainer && !overlaySymbolContainer.destroyed) {
            const trailContainer = animator.trailContainer;
            if (trailContainer && !trailContainer.destroyed) {
              _tempPoint.set(0, 0);
              const globalPos = overlaySymbolContainer.toGlobal(_tempPoint);
              const localPos = trailContainer.toLocal(globalPos);
              animator.fireTrail(symbolId, localPos, {
                onArrival: (potType) => {

                  const ctrl = potControllersRef?.current?.[potType];
                  if (!ctrl) return;
                  const state = store.getState().game;
                  const fs = state.featureState;
                  const isTrigger =
                    (fs?.type === "bonus") &&
                    fs?.phase === "intro";
                  // Only the ACTUALLY triggered pot type(s) get playTrigger
                  const typeToChar = { double: "D", mystery: "M", collect: "C" };
                  const charKey = typeToChar[potType]; // "D", "M", or "C"
                  const thisTypeTriggered = isTrigger &&
                    fs?.data?.respinGameType?.includes(charKey);

                  if (thisTypeTriggered) {
                    ctrl.playTrigger();
                  } else {
                    // Read API target stage from Redux — grow pot on bolt impact
                    const apiStage = state.potProgress?.[charKey]?.stage;
                    if (apiStage !== undefined && apiStage > ctrl.stage) {
                      // Check if this is a real visual growth (different animation)
                      // Stages 0 and 1 both use animation 1 — no visual change
                      const effectiveNew = Math.max(1, apiStage);
                      const effectiveCurrent = Math.max(1, ctrl.stage);
                      if (effectiveNew > effectiveCurrent) {
                        // Different animation level — full collect→in→idle chain
                        ctrl.setStage(apiStage);
                      } else {
                        // Same animation level (e.g., 0→1) — sync stage, hit only
                        ctrl.stage = apiStage;
                        ctrl.playHit();
                      }
                    } else {
                      // Stage already matches — play hit flash only (additional bolts this spin)
                      ctrl.playHit();
                    }
                  }
                },
                duration: undefined, // use default from config
                reelIndex,
                symbolIndex,
              });
            }
          }
        }

        // --- The Pot: a landed coin throws its trail at the logo (GDD §5.1) ---
        // Cleopatra's Fire. Same place and same rule as the Zeus branch above —
        // before the turbo skip, so it fires at every speed. The Mad Labs trail
        // (the base room's LevelChargingAnimator), the logo's centre as the
        // target, `logo_hit` on arrival. Line wins wait for it (useWinController).
        if (FEATURES_CONFIG.LOGO_POT?.TRAILS_ENABLED && isCoinSymbol(symbolId)) {
          const scene = gameViewControllerRef?.current;
          const sizer = goldenTrailAnimatorRef?.current;
          fireCoinToLogo({
            // The scene's layer under the logo (falls in behind it); the
            // golden trail layer if the spine has no such slot.
            layer: scene?.coinTrailContainer
              ? { trailContainer: scene.coinTrailContainer, symbolHeight: sizer?.symbolHeight }
              : sizer,
            from: overlayController?.overlaySymbolRefs?.[symbolIndex],
            target: scene?.getLogoTarget?.(),
            // onArrival: () => gameViewControllerRef?.current?.playLogoHit?.(),
            // The hit, and a point toward the next fire stage (logoPot).
            onArrival: () => {
              gameViewControllerRef?.current?.playLogoHit?.();
              addLogoPotPoint();
            },
          });
        }

        // Anticipation mode: only play landing animations for bonus symbols (Zeus coins).
        // Non-bonus symbols stay dark under the global DimReelSet overlay — no overlay
        // animation needed. Zeus coins get full landing→idle so they appear bright above dim.
        if (FEATURES_CONFIG.TENSION.ANTICIPATION?.ENABLED &&
            store.getState().game.currentRound?.anticipation === true &&
            !isBonusSymbol(symbolId)) {
          continue;
        }

        // Cleopatra's Fire: the same rule for the per-reel anticipation chain —
        // a reel stopping after the chain started keeps its non-trigger
        // symbols as pictures (Games/Shared/tensionSymbols, the landing gate).
        // ── OFF (Vladimir, 2026-09-30: revert the animation suppressing) ──
        // every reel lands with its full landing again.
        // if (skipTensionLanding(reelIndex, symbolIndex, symbolId)) {
        //   reelController.showSymbolTexture?.(symbolIndex);
        //   continue;
        // }

        // Turbo with landing animations disabled: skip all spine work, show static textures
        // EXCEPTION: When tension is active this spin, scatter/trigger symbols get full landing→idle
        // so the player sees animated scatters during tension sequences
        let forceLanding = false;
        if (isTurbo && !FEATURES_CONFIG.SPINE_ANIMATIONS.TURBO_LANDING_ENABLED) {
          const isInTensionMode = reelController.hasTensionThisSpin === true;
          const isTriggerSymbol = FEATURES_CONFIG.TENSION.ENABLED &&
            FEATURES_CONFIG.TENSION.TRIGGER_SYMBOL?.includes(symbolId);
          // Cleopatra's Fire: the anticipation chain's own trigger symbols
          // (coins / scatters with hope) get their spine in turbo too — their
          // wait clips need one (2026-10-07).
          const waitsInChain = anticipationWaitsFor(
            store.getState().game.currentRound,
            symbolId,
          );

          // if (!isInTensionMode || !isTriggerSymbol) {
          if ((!isInTensionMode || !isTriggerSymbol) && !waitsInChain) {
            // if (reelController.showSymbolTexture) {
            //   reelController.showSymbolTexture(symbolIndex);
            // }
            // The held `static` (see landsStatic) — its z-order is already set
            // above (applyOverlayZIndex), like every landed spine's.
            // if (landsStatic && overlayController.hasClip?.(symbolId, "static")) {
            //   overlayController.showIdleAnimation(symbolIndex, symbolId, betOverride, true, "static");
            //   textureHideOps.push({ reelController, symbolIndex });
            // } else if (reelController.showSymbolTexture) {
            // ── Its IDLE when it has one, else the `static` (2026-10-28: "when
            // we land and not hold spacebar we play idle and static") ── no
            // landing clip either way (Zeus GDD §7.2: "IN animation for
            // symbols is disabled" in turbo); the idle runs at once, unfrozen.
            if (landsStatic && overlayController.hasClip?.(symbolId, "idle")) {
              overlayController.showIdleAnimation(symbolIndex, symbolId, betOverride, false, "idle");
              textureHideOps.push({ reelController, symbolIndex });
            } else if (landsStatic && overlayController.hasClip?.(symbolId, "static")) {
              overlayController.showIdleAnimation(symbolIndex, symbolId, betOverride, true, "static");
              textureHideOps.push({ reelController, symbolIndex });
            } else if (reelController.showSymbolTexture) {
              reelController.showSymbolTexture(symbolIndex);
            }
            continue;
          } else {
            // Scatter during tension: force full landing→idle (not turbo idle-only)
            forceLanding = true;
          }
        }

        const { hasLanding, hasIdle } =
          overlayController.getAvailableAnimations(symbolIndex, symbolId);

        // Play landing sound based on symbol type
        // Super turbo: all reels land simultaneously — play each sound once per frame max
        if (isCoinSymbol(symbolId) && (hasLanding || hasIdle)) {
          if (isSuperTurboMode) {
            _playOncePerFrame(SOUND_NAME.c_in);
          } else {
            soundManager.play(SOUND_NAME.c_in);
          }
        } else if (isBonusSymbol(symbolId) && (hasLanding || hasIdle)) {
          if (isSuperTurboMode) {
            // Play one zeus landing sound for all zeus symbols this frame
            const zeusSound = symbolId === ZEUS_COLLECT_ID ? SOUND_NAME.cc_in
              : symbolId === ZEUS_DOUBLE_ID ? SOUND_NAME.cd_in
              : SOUND_NAME.cm_in;
            _playOncePerFrame(zeusSound);
          } else {
            if (symbolId === ZEUS_COLLECT_ID) soundManager.play(SOUND_NAME.cc_in);
            else if (symbolId === ZEUS_DOUBLE_ID) soundManager.play(SOUND_NAME.cd_in);
            else if (symbolId === ZEUS_MYSTERY_ID) soundManager.play(SOUND_NAME.cm_in);
          }
        }

        if (isTurbo && !forceLanding) {
          // Turbo: idle-only (skips landing animation entirely)
          if (hasIdle) {
            overlayController.showIdleAnimation(
              symbolIndex,
              symbolId,
              betOverride,
            );
            textureHideOps.push({ reelController, symbolIndex });
          } else {
            if (reelController.showSymbolTexture) {
              reelController.showSymbolTexture(symbolIndex);
            }
          }
        } else if (hasLanding || hasIdle) {
          // Round-level idle: show frozen overlay during landing so the swap from
          // texture→spine happens during the reel bounce (invisible to the user).
          // The idle animation stays frozen until all reels stop.
          if (FEATURES_CONFIG.SPINE_ANIMATIONS.ROUND_LEVEL_IDLE && !hasLanding) {
            if (hasIdle) {
              overlayController.showIdleAnimation(
                symbolIndex,
                symbolId,
                betOverride,
                true, // frozen — will be unfrozen when all reels stop
              );
              textureHideOps.push({ reelController, symbolIndex });
            }
          } else {

            // Normal: full landing→idle sequence (or landing→hold in round-level mode)
            overlayController.showLandingAnimation(
              symbolIndex,
              symbolId,
              betOverride,
            );
            textureHideOps.push({ reelController, symbolIndex });
          }
        } else {
          if (reelController.showSymbolTexture) {
            reelController.showSymbolTexture(symbolIndex);
          }
        }
      }

      // Fire jackpot bar highlight animation for highest tier landed this reel
      if (FEATURES_CONFIG.JACKPOTS.ENABLED && landedJackpotTier && jackpotsControllerRef?.current) {
        const ctrl = jackpotsControllerRef.current;
        if (landedJackpotTier === "mini") ctrl.playMini();
        else if (landedJackpotTier === "major") ctrl.playMajor();
        else if (landedJackpotTier === "grand") ctrl.playGrand();
      }

      // A reel landing UNHELD settles the reels that landed before it this
      // spin — a hold let go mid-landing left those on pictures (turboRest).
      if (
        isTurbo &&
        !FEATURES_CONFIG.SPINE_ANIMATIONS.TURBO_LANDING_ENABLED &&
        landsStatic
      ) {
        restTurboBoard(reelIndex);
      }

      // Deferred texture hide for landing animations only (need 1-frame overlap)
      if (!skipRAF && textureHideOps.length > 0) {
        requestAnimationFrame(() => {
          textureHideOps.forEach(({ reelController, symbolIndex }) => {
            if (reelController.hideSymbolTexture) {
              reelController.hideSymbolTexture(symbolIndex);
            }
          });
        });
      }

      return textureHideOps;
    },
    [
      overlayReelRefs,
      reelRefs,
      jackpotsControllerRef,
      potChargingAnimatorRef,
      potControllersRef,
      goldenTrailAnimatorRef,
      gameViewControllerRef,
      restTurboBoard,
    ],
  );

  /**
   * Resolve once every landing bounce this hook started has finished.
   *
   * ── The landing is PART OF THE SPIN ──
   *
   * The round is not over while the board is still moving. This is what lets
   * ReelControllerV2 hold `setSpinComplete` until the bounces are done, so the
   * spin button, the win presentation and autoplay all reopen on the same
   * signal instead of three different ones — see _completeSpin there.
   *
   * It belongs in THIS hook because this is what starts the landings; asking
   * anywhere else means a second place that has to know which reels animate.
   *
   * Costs nothing when there is nothing to wait for: turbo and super turbo
   * suppress landings entirely, so no reel registers one and this resolves on
   * the next microtask.
   */
  const waitForReelLandings = useCallback(
    () =>
      waitForBoardToSettle(
        overlayReelRefs.current?.map((r) => r?.getOverlayReelController?.()),
      ),
    [overlayReelRefs],
  );

  return { triggerReelLandingAnimations, waitForReelLandings };
};

/**
 * Hook for triggering idle animations.
 * Default mode: Shows idle animations for all symbols that have them.
 * Transition mode: Only transitions active win animations to idle.
 */
export const useInitialIdleAnimations = () => {
  const { reelRefs, overlayReelRefs, dimReelSetRef } = useReelRefs();

  const triggerInitialIdleAnimations = useCallback(
    // `ignoreSpeed` (2026-10-13): idle the board whatever the speed — the way
    // back from a bonus, which has just restored a turbo / super turbo the
    // player entered on (TransitionDialog). Every other caller keeps the rule.
    (onlyTransitionWinAnimations = false, { ignoreSpeed = false } = {}) => {
      for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
        const overlayReelRef = overlayReelRefs.current?.[reelIndex];
        const overlayController = overlayReelRef?.getOverlayReelController();
        const reelController =
          reelRefs.current?.[reelIndex]?.getReelController();

        if (!overlayController || !reelController) {
          continue;
        }

        if (onlyTransitionWinAnimations) {
          if (overlayController.isExpandingWildVisible) {
            overlayController.forceStopExpandingWildFullReel?.();
          }

          for (
            let symbolIndex = 0;
            symbolIndex < Settings.SYMBOLS_VISIBLE;
            symbolIndex++
          ) {
            const symbol =
              reelController.symbolsContainer.children[symbolIndex];
            const symbolId = symbol?.currentSymbolId || symbol?.symbolId;

            if (symbolId === undefined) {
              continue;
            }

            applyOverlayZIndex(overlayController, symbolIndex, symbolId);
            if (symbolId === COIN_JACKPOT_SYMBOL_ID) {
              wearJackpotCoinSkin(
                overlayController,
                store.getState().game.currentRound,
                reelIndex,
                symbolIndex,
              );
            }

            const { hasIdle } = overlayController.getAvailableAnimations(
              symbolIndex,
              symbolId,
            );

            // ── A SETTLED picture is a texture, never a spine ──
            //
            // A cell painted with setSymbolTextureOnly keeps its symbol id —
            // the gamble scatter stays 12 — but shows what it settled on: the
            // open window with the package, a Golden composite. The idle for
            // that id is the CLOSED door (`scg`'s `static`, which
            // getAvailableAnimations counts as an idle), so starting it put the
            // doors back over the window. Vladimir, 2026-09-24, on a lost
            // gamble reloaded: *"we should have only textures."* It goes to the
            // no-idle branch instead: animation off, texture on.
            if (hasIdle && !symbol?.hasStaticOverride) {
              // Smooth crossfade from win → idle (0.3s mix, no snap)
              overlayController.transitionToIdle(symbolIndex, symbolId);
              if (reelController.hideSymbolTexture) {
                reelController.hideSymbolTexture(symbolIndex);
              }
            } else {
              // No idle — stop win animation, restore texture
              overlayController.hideWinAnimation(symbolIndex);
              if (reelController.showSymbolTexture) {
                reelController.showSymbolTexture(symbolIndex);
              }
            }
          }
        } else {
          // // In turbo mode, skip all idle animations — symbols stay as static textures
          // const isTurbo = isTurboLike(store.getState().settings.autoPlaySpeed);
          // if (isTurbo && !FEATURES_CONFIG.SPINE_ANIMATIONS.TURBO_LANDING_ENABLED) {
          // if (
          //   !ignoreSpeed &&
          //   isTurbo &&
          //   !FEATURES_CONFIG.SPINE_ANIMATIONS.TURBO_LANDING_ENABLED
          // ) {
          //   baseDiag(`idle on load — reel ${reelIndex} SKIPPED (turbo speed)`);
          //   continue;
          // }
          // ── Off (2026-10-30, QA: "symbol animations are missing after
          // refreshing in Turbo / Super Turbo") ── left from when turbo had
          // no spines at all. Turbo rests its board on idle / static now
          // (Games/Shared/turboRest), and every caller here is a one-off
          // board restore (reload, bonus exit, replay, recovery), never a
          // held run's spin — so the board idles whatever the speed.

          for (
            let symbolIndex = 0;
            symbolIndex < Settings.SYMBOLS_VISIBLE;
            symbolIndex++
          ) {
            const symbol =
              reelController.symbolsContainer.children[symbolIndex];
            const symbolId = symbol?.currentSymbolId || symbol?.symbolId;

            if (symbolId === undefined) {
              continue;
            }

            applyOverlayZIndex(overlayController, symbolIndex, symbolId);
            if (symbolId === COIN_JACKPOT_SYMBOL_ID) {
              wearJackpotCoinSkin(
                overlayController,
                store.getState().game.currentRound,
                reelIndex,
                symbolIndex,
              );
            }

            const { hasIdle } = overlayController.getAvailableAnimations(
              symbolIndex,
              symbolId,
            );
            baseDiag(`idle on load — cell [${reelIndex},${symbolIndex}]`, {
              symbolId,
              hasIdle,
              staticOverride: !!symbol?.hasStaticOverride,
              availabilityKnown: overlayController.animationAvailability?.has(`0_${symbolId}`),
            });

            // Same rule as the transition branch above: a settled picture
            // keeps its texture. This is the call the intro popup makes when it
            // closes, which is where the doors came back on a reload.
            if (hasIdle && symbol?.hasStaticOverride) {
              baseDiag(
                `idle skipped — settled cell [${reelIndex},${symbolIndex}] keeps its texture`,
              );
            } else if (hasIdle) {
              // Check if a landing animation is currently playing on this symbol
              const activeAnim = overlayController.activeAnimations?.get(symbolIndex);
              const wasHoldingLanding = activeAnim?.animationType === "landing";

              if (wasHoldingLanding && activeAnim?.spineInstance?.state) {
                // Landing still in progress — queue idle AFTER landing completes
                // instead of interrupting it with showIdleAnimation
                const spineInstance = activeAnim.spineInstance;
                const idleAnimation = overlayController.selectSpineAnimation(
                  spineInstance,
                  "idle",
                  symbolId,
                );
                if (idleAnimation) {
                  spineInstance.state.addAnimation(0, idleAnimation, true, 0);
                  // Update tracking so downstream code knows this is now idle
                  overlayController.activeAnimations.set(symbolIndex, {
                    ...activeAnim,
                    animationType: "idle",
                  });
                }
              } else {
                overlayController.showIdleAnimation(symbolIndex, symbolId);

                if (!wasHoldingLanding) {
                  // Idle-only: hide texture after 1 frame — allows spine GPU textures
                  // to upload before texture disappears. The 1-frame overlap is invisible
                  // since both show the same symbol image.
                  requestAnimationFrame(() => {
                    if (reelController.hideSymbolTexture) {
                      reelController.hideSymbolTexture(symbolIndex);
                    }
                  });
                }
              }
            }
          }
        }
      }

      if (onlyTransitionWinAnimations && dimReelSetRef.current) {
        dimReelSetRef.current.undimReels();
      }
    },
    [overlayReelRefs, reelRefs, dimReelSetRef],
  );

  return { triggerInitialIdleAnimations };
};

/**
 * Hook for triggering idle animations on free spin reels.
 * Mirrors triggerInitialIdleAnimations(true) but targets free spin reel refs.
 * Hides win animations, transitions to idle, undims, hides win lines/text.
 */
export const useFreeSpinIdleAnimations = () => {
  const {
    freeSpinReelRefs,
    freeSpinOverlayReelRefs,
    dimReelSetRef,
    winLinesContainerRef,
    lineWinTextRef,
    globalLineWinTextRef,
  } = useReelRefs();

  const triggerFreeSpinIdleAnimations = useCallback(() => {
    for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
      const overlayReelRef = freeSpinOverlayReelRefs.current?.[reelIndex];
      const overlayController =
        overlayReelRef?.getFreeSpinOverlayReelController();
      const reelController =
        freeSpinReelRefs.current?.[reelIndex]?.getFreeSpinReelController();

      if (!overlayController || !reelController) {
        continue;
      }

      for (
        let symbolIndex = 0;
        symbolIndex < Settings.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        // Read symbol ID from the reel controller sprites (VISIBLE_START = 1)
        const spriteData = reelController.spinningSprites?.[1 + symbolIndex];
        const symbolId = spriteData?.currentSymbolId ?? spriteData?.symbolId;

        if (symbolId === undefined) {
          continue;
        }

        const { hasIdle } = overlayController.getAvailableAnimations?.(
          symbolIndex,
          symbolId,
        ) || {};

        if (hasIdle) {
          // Smooth crossfade from win → idle, hide texture
          if (overlayController.transitionToIdle) {
            overlayController.transitionToIdle(symbolIndex, symbolId);
          } else {
            overlayController.showIdleAnimation(symbolIndex, symbolId);
          }
          reelController.hideSymbolTexture?.(symbolIndex);
        } else {
          // No idle — stop win animation, restore texture
          overlayController.hideWinAnimation?.(symbolIndex);
          reelController.showSymbolTexture?.(symbolIndex);
        }
      }
    }

    // Undim reels
    if (dimReelSetRef.current) {
      dimReelSetRef.current.undimReels();
    }

    // Hide win lines
    if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
      winLinesContainerRef.current.hideWinLine();
    }

    // Hide win text
    if (lineWinTextRef?.current) lineWinTextRef.current.hide();
    if (globalLineWinTextRef?.current) globalLineWinTextRef.current.hide();
  }, [
    freeSpinReelRefs,
    freeSpinOverlayReelRefs,
    dimReelSetRef,
    winLinesContainerRef,
    lineWinTextRef,
    globalLineWinTextRef,
  ]);

  return { triggerFreeSpinIdleAnimations };
};

/**
 * Hook for triggering landing animations for individual Hold & Win symbols.
 * Works on a per-symbol basis rather than per-reel.
 */
export const useHoldAndWinLandingAnimations = () => {
  const { holdAndWinReelRefs, holdAndWinOverlayReelRefs } = useReelRefs();

  const triggerHoldAndWinSymbolLandingAnimation = useCallback(
    (symbolIndex, reelIndex, symbolId, onTextureHiddenCallback) => {
      const overlayReelRef = holdAndWinOverlayReelRefs.current?.[reelIndex];
      const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
      const overlaySymbolController = overlaySymbolRef?.getController?.();

      const holdAndWinReelRef = holdAndWinReelRefs.current?.[reelIndex];
      const symbolRef = holdAndWinReelRef?.getSymbolRef?.(symbolIndex);
      const symbolController = symbolRef?.current?.getSymbolController?.();

      if (!overlaySymbolController || !symbolController) {
        if (onTextureHiddenCallback) {
          onTextureHiddenCallback();
        }
        return;
      }

      if (COIN_SYMBOL_IDS.includes(symbolId)) {
        const animationStarted = overlaySymbolController.showLandingAnimation(symbolId);

        if (animationStarted) {
          const spinningContainer = symbolController.spinningContainer;
          const staticCoinSprite = symbolController.staticCoinSprite;
          requestAnimationFrame(() => {
            const containerAlive = spinningContainer && !spinningContainer.destroyed;
            const staticAlive = staticCoinSprite?.normalSprite && !staticCoinSprite.normalSprite.destroyed;
            if (containerAlive || staticAlive) {
              symbolController.hideSymbolTexture();
            }

            if (onTextureHiddenCallback) {
              onTextureHiddenCallback();
            }
          });
        } else {
          if (onTextureHiddenCallback) {
            onTextureHiddenCallback();
          }
        }
      }
    },
    [holdAndWinReelRefs, holdAndWinOverlayReelRefs],
  );

  return { triggerHoldAndWinSymbolLandingAnimation };
};

/**
 * Hook for triggering idle animations for all Hold & Win symbols at once.
 * Used during Hold and Win reload to show idle animations for existing coins.
 */
export const useHoldAndWinIdleAnimations = () => {
  const { holdAndWinReelRefs, holdAndWinOverlayReelRefs } = useReelRefs();

  const triggerHoldAndWinIdleAnimations = useCallback(() => {
    for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
      for (
        let symbolIndex = 0;
        symbolIndex < Settings.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        const overlayReelRef = holdAndWinOverlayReelRefs.current?.[reelIndex];
        const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
        const overlaySymbolController = overlaySymbolRef?.getController?.();

        const holdAndWinReelRef = holdAndWinReelRefs.current?.[reelIndex];
        const symbolRef = holdAndWinReelRef?.getSymbolRef?.(symbolIndex);

        if (!symbolRef || !symbolRef.current) {
          continue;
        }

        const symbolController = symbolRef.current.getSymbolController?.();

        if (!overlaySymbolController || !symbolController) {
          continue;
        }

        const symbolId = symbolController.symbolId;
        if (symbolId && COIN_SYMBOL_IDS.includes(symbolId)) {
          const animationStarted = overlaySymbolController.showIdleAnimation(symbolId);

          if (animationStarted) {
            const spinningContainer = symbolController.spinningContainer;
            const staticCoinSprite = symbolController.staticCoinSprite;
            requestAnimationFrame(() => {
              const containerAlive = spinningContainer && !spinningContainer.destroyed;
              const staticAlive = staticCoinSprite?.normalSprite && !staticCoinSprite.normalSprite.destroyed;
              if (containerAlive || staticAlive) {
                symbolController.hideSymbolTexture();
              }
            });
          }
        }
      }
    }
  }, [holdAndWinReelRefs, holdAndWinOverlayReelRefs]);

  return { triggerHoldAndWinIdleAnimations };
};

/**
 * Hook for triggering WIN animations for coins on base game overlay reels.
 * Used during Hold and Win intro reload to show win animations for trigger coins.
 */
export const useTriggerCoinWinAnimations = () => {
  const { reelRefs, overlayReelRefs, dimReelSetRef } = useReelRefs();

  const triggerCoinWinAnimations = useCallback(() => {
    let hasCoinAnimations = false;

    for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
      const reelRef = reelRefs.current?.[reelIndex];
      const overlayReelRef = overlayReelRefs.current?.[reelIndex];

      if (!reelRef || !overlayReelRef) {
        continue;
      }

      const reelController = reelRef.getReelController?.();
      const overlayReelController = overlayReelRef.getOverlayReelController?.();

      if (!reelController || !overlayReelController) {
        continue;
      }

      for (
        let symbolIndex = 0;
        symbolIndex < Settings.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        const symbol = reelController.symbolsContainer?.children?.[symbolIndex];

        if (!symbol) {
          continue;
        }

        const symbolId = symbol.currentSymbolId || symbol.symbolId;

        if (symbolId && COIN_SYMBOL_IDS.includes(symbolId)) {
          applyOverlayZIndex(overlayReelController, symbolIndex, symbolId);
          overlayReelController.showWinAnimation(symbolIndex, symbolId);
          hasCoinAnimations = true;

          if (reelController.hideSymbolTexture) {
            reelController.hideSymbolTexture(symbolIndex);
          }
        }
      }
    }

    // Only dim if coins were actually found — prevents black overlay
    // when base game has no coins (e.g. mid-bonus reload with stripped wins)
    if (hasCoinAnimations && dimReelSetRef.current) {
      dimReelSetRef.current.dimReels();
    }
  }, [reelRefs, overlayReelRefs, dimReelSetRef]);

  return { triggerCoinWinAnimations };
};

/**
 * Hook for triggering dark animations for all Hold & Win symbols at once.
 * Used during Hold and Win completion reload to show collected state (dark coins).
 */
export const useHoldAndWinCompletionIdleAnimations = () => {
  const { holdAndWinReelRefs, holdAndWinOverlayReelRefs } = useReelRefs();

  const triggerHoldAndWinCompletionIdleAnimations = useCallback(() => {
    for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
      for (
        let symbolIndex = 0;
        symbolIndex < Settings.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        const overlayReelRef = holdAndWinOverlayReelRefs.current?.[reelIndex];
        const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
        const overlaySymbolController = overlaySymbolRef?.getController?.();

        const holdAndWinReelRef = holdAndWinReelRefs.current?.[reelIndex];
        const symbolRef = holdAndWinReelRef?.getSymbolRef?.(symbolIndex);

        if (!symbolRef || !symbolRef.current) {
          continue;
        }

        const symbolController = symbolRef.current.getSymbolController?.();

        if (!overlaySymbolController || !symbolController) {
          continue;
        }

        const symbolId = symbolController.symbolId;

        if (symbolId && COIN_SYMBOL_IDS.includes(symbolId)) {
          const animationStarted = overlaySymbolController.showDarkAnimation(symbolId);

          if (animationStarted) {
            const spinningContainer = symbolController.spinningContainer;
            const staticCoinSprite = symbolController.staticCoinSprite;
            requestAnimationFrame(() => {
              const containerAlive = spinningContainer && !spinningContainer.destroyed;
              const staticAlive = staticCoinSprite?.normalSprite && !staticCoinSprite.normalSprite.destroyed;
              if (containerAlive || staticAlive) {
                symbolController.hideSymbolTexture();
              }
            });
          }
        } else {
          if (symbolController.showSymbolTexture) {
            symbolController.showSymbolTexture();
          }
        }
      }
    }
  }, [holdAndWinReelRefs, holdAndWinOverlayReelRefs]);

  return { triggerHoldAndWinCompletionIdleAnimations };
};

/**
 * Hook for triggering idle animations for all Pot Collection symbols at once.
 * Used during Pot Collection entry to show idle animations for starting board.
 * Only coin, jackpot, and zeus orb symbols get spine overlays — blanks skip.
 *
 * Called from PotCollectionControlContext after transition reveals the reelset.
 */
export const usePotCollectionIdleAnimations = () => {
  const { potCollectionReelRefs, potCollectionOverlayReelRefs } = useReelRefs();

  const triggerPotCollectionIdleAnimations = useCallback(() => {
    for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
      const overlayReelRef = potCollectionOverlayReelRefs.current?.[reelIndex];
      const reelRef = potCollectionReelRefs.current?.[reelIndex];

      if (!overlayReelRef || !reelRef) continue;

      for (
        let symbolIndex = 0;
        symbolIndex < Settings.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        const overlaySymbolRef = overlayReelRef.getSymbolRef?.(symbolIndex);
        const symbolRef = reelRef.getSymbolRef?.(symbolIndex);

        if (!overlaySymbolRef || !symbolRef?.current) continue;

        // Read symbolId from the static symbol's current state
        // For PC, we use the imperative ref API (no spinning controller)
        const symbolController = symbolRef.current;
        // PC symbols don't have getSymbolController — they store symbolId directly
        // We need to get the symbolId from the initial reels data via Redux
        const state = store.getState();
        const initialReels = state.potCollection?.initialRespinScreen;
        if (!initialReels?.[reelIndex]) continue;

        const symbolId = initialReels[reelIndex][symbolIndex];
        if (symbolId === undefined || symbolId === null) continue;

        // Only coin/jackpot/zeus symbols get overlay spine animations
        if (!COIN_SYMBOL_IDS.includes(symbolId) && !isJackpotSymbol(symbolId) && !isBonusSymbol(symbolId)) continue;

        if (overlaySymbolRef.showIdleAnimation) {
          overlaySymbolRef.showIdleAnimation(symbolId);
        }

        // Hide static texture underneath (spine overlay takes over)
        if (symbolController.hideSymbolTexture) {
          requestAnimationFrame(() => {
            symbolController.hideSymbolTexture();
          });
        }
      }
    }
  }, [potCollectionReelRefs, potCollectionOverlayReelRefs]);

  return { triggerPotCollectionIdleAnimations };
};

/**
 * Hook for triggering landing animations for individual Pot Collection symbols.
 * Works on a per-symbol basis — called when a coin lands during respins.
 *
 * Called from PotCollectionControlContext after spin stop.
 */
export const usePotCollectionLandingAnimations = () => {
  const { potCollectionReelRefs, potCollectionOverlayReelRefs } = useReelRefs();

  const triggerPotCollectionSymbolLandingAnimation = useCallback(
    (symbolIndex, reelIndex, symbolId, onTextureHiddenCallback) => {
      const overlayReelRef = potCollectionOverlayReelRefs.current?.[reelIndex];
      const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);

      const reelRef = potCollectionReelRefs.current?.[reelIndex];
      const symbolRef = reelRef?.getSymbolRef?.(symbolIndex);

      if (!overlaySymbolRef || !symbolRef?.current) {
        if (onTextureHiddenCallback) {
          onTextureHiddenCallback();
        }
        return;
      }

      // Coin/jackpot/zeus: show landing animation on overlay, hide static texture
      if (COIN_SYMBOL_IDS.includes(symbolId) || isJackpotSymbol(symbolId) || isBonusSymbol(symbolId)) {
        if (overlaySymbolRef.showLandingAnimation) {
          overlaySymbolRef.showLandingAnimation(symbolId);
        }

        requestAnimationFrame(() => {
          if (symbolRef.current?.hideSymbolTexture) {
            symbolRef.current.hideSymbolTexture();
          }
          if (onTextureHiddenCallback) {
            onTextureHiddenCallback();
          }
        });
      } else {
        // Blank or non-overlay symbol — just show static texture
        if (symbolRef.current?.showSymbolTexture) {
          symbolRef.current.showSymbolTexture();
        }
        if (onTextureHiddenCallback) {
          onTextureHiddenCallback();
        }
      }
    },
    [potCollectionReelRefs, potCollectionOverlayReelRefs],
  );

  return { triggerPotCollectionSymbolLandingAnimation };
};
